import EventEmitter from "eventemitter3";

import LegacyDispatcher from "~/lib/legacyDispatcher";

import JobLogGroupStore from "~/stores/JobLogGroupStore";
import JobLogLineStore from "~/stores/JobLogLineStore";

const normalizeUrl = function (url) {
  const withoutAnchor = url.split("#")[0];

  // Normalize the /
  if (withoutAnchor[0] === "/") {
    return withoutAnchor;
  }

  return `/${withoutAnchor}`;
};

const navigateToUrl = function (url) {
  const currentUrl = normalizeUrl(window.location.pathname);
  const newUrl = normalizeUrl(url);

  // If the URL's are the same, just update the anchor, otherwise, navigate to
  // the new page.
  if (currentUrl === newUrl) {
    const newAnchor = url.split("#")[1];
    if (newAnchor != null) {
      return (window.location.hash = newAnchor);
    }
  } else {
    return (window.location = url);
  }
};

const updateAnchorWithoutNavigation = (hash: any) => {
  const url = new URL(window.location.href);
  url.hash = hash;
  history.replaceState(null, "", url.toString());
};

// If we make it blank, the page will jump to the top.
const clearAnchor = () => (window.location.hash = "_");

/**
 * Scroll to an element after a timeout to allow the page to render.
 *
 * Used to scroll to a job, group, or line after it's been expanded.
 */
const scrollToElement = (el: HTMLElement) => {
  if (!Features.RefactorLogScrollBehaviour) {
    setTimeout(() => {
      el?.scrollIntoView(true);
    }, 250);
  }
};

type ParsedHash = {
  job: string;
  group: string | undefined;
  line: string | undefined;
  endLine: string | undefined;
} | null;

/**
 * Parse URL hash for job log attributes.
 *
 * Supports URL formats:
 * - Line-range: `#jobId/L10-L15` → `{ job, line: "10", endLine: "15" }` (always normalized to ascending)
 * - Line-only: `#jobId/L54` → `{ job, line: "54" }` (group and endLine undefined)
 * - Group-line: `#jobId/53-54` → `{ job, group: "53", line: "54" }`
 * - Group-only: `#jobId/53` → `{ job, group: "53" }` (line undefined)
 * - Job-only: `#jobId` → `{ job }` (group, line, endLine undefined)
 *
 * Line-only format can be inferred by: group === undefined && line !== undefined
 * Line-range format can be inferred by: endLine !== undefined
 */
export const parseHash = (hash: string): ParsedHash => {
  // Try line-range format: jobId/L{start}-L{end} (e.g., L10-L15)
  const lineRangeMatch = hash.match(/([^/#]+)\/L(\d+)-L(\d+)/);
  if (lineRangeMatch) {
    const [, job, startLine, endLineStr] = lineRangeMatch;
    // Normalize to ascending order
    const start = parseInt(startLine, 10);
    const end = parseInt(endLineStr, 10);
    if (start <= end) {
      return {
        job,
        group: undefined,
        line: startLine,
        endLine: endLineStr,
      };
    }
    // Swap if in descending order (L15-L10 → line: "10", endLine: "15")
    return {
      job,
      group: undefined,
      line: endLineStr,
      endLine: startLine,
    };
  }

  // Try line-only format: jobId/L{lineNumber}
  const lineOnlyMatch = hash.match(/([^/#]+)\/L(\d+)/);
  if (lineOnlyMatch) {
    const [, job, line] = lineOnlyMatch;
    return {
      job,
      group: undefined,
      line,
      endLine: undefined,
    };
  }

  // Fall back to group-line format: jobId/{group}-{line} or jobId/{group} or jobId
  const groupLineMatch = hash.match(/([^/#]+)\/?(\d+)?-?(\d+)?/);
  if (!groupLineMatch) {
    return null;
  }

  const [, job, group, line] = groupLineMatch;
  return {
    job,
    group,
    line,
    endLine: undefined,
  };
};

type Job = {
  id: string;
};

class BuildPageStore {
  eventEmitter: EventEmitter;
  state: {
    expands: {
      [key: string]: boolean;
    };
    line: { job: string; line: string } | null | undefined;
  };

  // TODO: these vars can be removed when removing Features.RefactorLogScrollBehaviour
  needsScrollingToLine = false;
  scrollToJobAfterRender: string | null | undefined = null;

  collapseJobs(jobs: Job[]) {
    jobs.forEach((job) => {
      this.state.expands[job.id] = false;
    });

    return this.eventEmitter.emit("change");
  }

  expandJob(job: Job) {
    this.state.expands[job.id] = true;

    if (!Features.RefactorLogScrollBehaviour) {
      const element = document.getElementById(`job-${job.id}`);
      if (!element) {
        return;
      }
      const rect = element.getBoundingClientRect();
      if (rect.top < 0 || rect.top >= window.innerHeight) {
        element.scrollIntoView();
      }
    }

    return this.eventEmitter.emit("change");
  }

  constructor() {
    this.eventEmitter = new EventEmitter();
    this.state = {
      expands: {},
      line: null,
    };

    if (!Features.RefactorLogScrollBehaviour) {
      this.needsScrollingToLine = false;
      this.scrollToJobAfterRender = null;

      window.addEventListener("DOMContentLoaded", () => {
        this.scrollToJobAfterRender = null;
        this.needsScrollingToLine = false;

        const anchor = parseHash(window.location.hash);
        if (anchor) {
          const { job, line, group } = anchor;

          this.state.expands[job] = true;
          this.scrollToJobAfterRender = job;

          // Expand the group the line is in, also highlight the group
          if (group) {
            const groupId = `${job}/${group}`;

            JobLogGroupStore.setDefault(groupId, true);
            JobLogLineStore.highlight(groupId);

            // Highlight the line
            if (line) {
              const lineId = `${groupId}-${line}`;
              JobLogLineStore.highlight(lineId);
            }

            // We need to scroll to the line after it's been rendered
            this.needsScrollingToLine = true;
          }

          return this.eventEmitter.emit("change");
        }
      });
    }

    LegacyDispatcher.on("job:toggle", ({ job }) => {
      const isExpanded = this.state.expands[job.id];

      if (isExpanded) {
        this.state.expands[job.id] = false;

        // New build#show ui does not use anchors to navigate between jobs, so we can skip this behaviour
        // Note: It's not necessary, but keeps the url clean when expand/collapsing jobs
        if (!Features.BuildSidebar) {
          clearAnchor();
        }
      } else {
        this.state.expands[job.id] = true;
      }

      return this.eventEmitter.emit("change");
    });

    LegacyDispatcher.on("job:focus", ({ job }) => {
      this.state.expands[job.id] = true;
      navigateToUrl(job.path);
      if (!Features.RefactorLogScrollBehaviour) {
        this.scrollToJobAfterRender = job.id;
      }
      return this.eventEmitter.emit("change");
    });

    if (!Features.RefactorLogScrollBehaviour) {
      // Watch for when a job is rendered so we can jump to it if we need to
      // (unless we're still waiting for a highlight)
      LegacyDispatcher.on("job:expanded", ({ job, el }) => {
        if (this.scrollToJobAfterRender === job.id && !this.needsScrollingToLine) {
          if (!Features.BuildSidebar) {
            scrollToElement(el);
          }

          return (this.scrollToJobAfterRender = null);
        }
      });
    }

    // Show the new job when they click on the "Retried In" link
    LegacyDispatcher.on("job:retried-link-click", ({ job }) => {
      this.state.expands[job.id] = false;
      clearAnchor();
      this.state.expands[job.retriedInJobUuid] = true;
      navigateToUrl(window.location.pathname + `#${job.retriedInJobUuid}`);
      return this.eventEmitter.emit("change");
    });

    // Modify the current location anchor when a line is highlighted
    LegacyDispatcher.on("job_log_line:highlight", ({ lineId }) => {
      const [job, line] = lineId.split("/");

      // If we're turning off the current line
      if (this.state.line != null && this.state.line.job === job && this.state.line.line === line) {
        this.state.line = null;
        return updateAnchorWithoutNavigation(job);
      }

      this.state.line = { job, line };

      if (Features.LogViewerNewLineUrlFormat) {
        // line can be either:
        // - "groupNumber-lineNumber" for content lines (e.g., "53-54")
        // - "groupNumber" for group headers (e.g., "53")
        // Extract the actual line number for use in the line-only URL format
        const parts = line.split("-");
        const lineNumber = parts.length > 1 ? parts[1] : parts[0];
        return updateAnchorWithoutNavigation(`${job}/L${lineNumber}`);
      }

      return updateAnchorWithoutNavigation(lineId);
    });

    // Handle range highlighting (shift+click selection)
    LegacyDispatcher.on(
      "job_log_line:highlight_range",
      ({ jobId, startLine, endLine }: { jobId: string; startLine: number; endLine: number }) => {
        this.state.line = { job: jobId, line: `L${startLine}-L${endLine}` };
        return updateAnchorWithoutNavigation(`${jobId}/L${startLine}-L${endLine}`);
      },
    );

    if (!Features.RefactorLogScrollBehaviour) {
      // Watch for when a line is rendered highlighted so we can scroll to it if we should
      LegacyDispatcher.on("job_log_line:highlighted", ({ el, lineId }) => {
        if (lineId) {
          const [job, line] = lineId.split("/");
          this.state.line = { job, line };
        }

        if (this.needsScrollingToLine) {
          if (!Features.BuildSidebar) {
            scrollToElement(el);
          }

          this.scrollToJobAfterRender = null;
          return (this.needsScrollingToLine = false);
        }
      });

      // Watch for when a group is rendered highlighted so we can scroll to it if we should
      // Pretty much the same as lines
      LegacyDispatcher.on("job_log_group:highlighted", ({ el }) => {
        if (this.needsScrollingToLine) {
          if (!Features.BuildSidebar) {
            scrollToElement(el);
          }

          this.scrollToJobAfterRender = null;
          return (this.needsScrollingToLine = false);
        }
      });
    }
  }

  addChangeListener(job: Job | null | undefined, callback: any) {
    return this.eventEmitter.addListener("change", callback);
  }

  removeChangeListener(job: Job | null | undefined, callback: any) {
    return this.eventEmitter.removeListener("change", callback);
  }

  isJobExpanded(job: Job) {
    return this.state.expands[job.id] === true;
  }
}

export default new BuildPageStore();
